import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useInvoicesQuery } from './queries/useInvoicesQuery';
import { isFeatureEnabled } from '@/config/features';
import { useAuth } from './useAuth';

export interface Invoice {
    id: string;
    stripe_id: string;
    description: string;
    date: string;
    amount: number;
    currency: string;
    status: 'paid' | 'pending' | 'cancelled' | 'failed' | 'unknown';
    pdf_url?: string;
    hosted_url?: string;
    payment_intent_id?: string;
}

export const useInvoices = (organizationId: string) => {
    const { user } = useAuth();
    const [oldInvoices, setOldInvoices] = useState<Invoice[]>([]);
    const [oldLoading, setOldLoading] = useState(false);
    const [oldError, setOldError] = useState<string | null>(null);

    // Vérifier si on utilise le nouveau système
    const useNewSystem = isFeatureEnabled('REACT_QUERY_REFETCH', user?.id) || 
                          isFeatureEnabled('REALTIME_ENABLED', user?.id);

    // Nouveau système (React Query)
    const newInvoices = useInvoicesQuery(organizationId);

    // Ancien système (useEffect)
    const fetchInvoices = useCallback(async () => {
        if (!organizationId || useNewSystem) return;

        setOldLoading(true);
        setOldError(null);

        try {
            const { data, error } = await supabase.functions.invoke('get-invoices', {
                body: {
                    organization_id: organizationId,
                    limit: 50
                }
            });

            if (error) {
                throw new Error(error.message || 'Erreur lors de la récupération des factures');
            }

            setOldInvoices(data.invoices || []);
        } catch (err) {
            console.error('Error fetching invoices:', err);
            setOldError(err instanceof Error ? err.message : 'Erreur inconnue');
        } finally {
            setOldLoading(false);
        }
    }, [organizationId, useNewSystem]);

    useEffect(() => {
        if (!useNewSystem) {
            fetchInvoices();
        }
    }, [fetchInvoices, useNewSystem]);

    const downloadInvoice = (invoice: Invoice) => {
        if (invoice.pdf_url) {
            window.open(invoice.pdf_url, '_blank');
        } else if (invoice.hosted_url) {
            window.open(invoice.hosted_url, '_blank');
        }
    };

    // Choisir le système selon feature flag
    const invoices = useNewSystem ? (newInvoices.data || []) : oldInvoices;
    const loading = useNewSystem ? newInvoices.isLoading : oldLoading;
    const error = useNewSystem ? (newInvoices.error as Error)?.message : oldError;
    const refetch = useNewSystem ? newInvoices.refetch : fetchInvoices;

    return {
        invoices,
        loading,
        error,
        refetch,
        downloadInvoice
    };
};